import {DOM} from "../DOM.js";

export class Modals {

	/**
	 * HTML Content of header
	 * @type {string}
	 */
	#headerContent = '';

	/**
	 * HTML Content of body
	 * @type {string}
	 */
	#bodyContent = '';

	/**
	 * HTML Content of footer
	 * @type {string}
	 */
	#footerContent = '';

	/**
	 * Animation type
	 * @type {'ttb' | 'btt' | 'rtl' | 'ltr' | 'scale'}
	 */
	#animation = 'ttb';

	/**
	 * @type {boolean}
	 */
	#closeOnOutsideClick = false;

	/**
	 * Callback for onClose
	 *
	 * @param context{'outside' | 'header' | ''}
	 */
	#onClose = (context = '', event, target) => {}

	/**
	 * Popup classname
	 * @type {string}
	 */
	#className = '';

	/**
	 * @type {boolean}
	 */
	#headerHasCloseButton = true;

	/**
	 * Backdrop HTML Element
	 * @type {null|HTMLDivElement}
	 */
	backdrop = null;

	/**
	 * Modal HTML Element
	 * @type {null|HTMLDivElement}
	 */
	modal = null;

	/**
	 *
	 * Modal type
	 *
	 * @type {'popup', 'side'}
	 */
	#type = 'popup';

	/**
	 * @param close{boolean}
	 *
	 * @returns {Modals}
	 */
	closeOnOutsideClick(close) {
		this.#closeOnOutsideClick = close
		return this;
	}

	/**
	 * Set animation type
	 * @param type{'ttb' | 'btt' | 'rtl' | 'ltr' | 'scale'}
	 *
	 * @returns {Modals}
	 */
	animation(type = 'ttb') {
		this.#animation = type;
		return this;
	}

	/**
	 * Set on close callback
	 * @param callback{(context: 'outside' | 'header' | '') => void}
	 *
	 * @returns {Modals}
	 */
	setOnClose(callback) {
		this.#onClose = callback;
		return this;
	}

	/**
	 * Set modal's class names
	 *
	 * @param className{string}
	 * @returns {Modals}
	 */
	className(className) {
		this.#className = className;
		return this;
	}

	/**
	 * Set modal type
	 * @param type{'popup', 'side'}
	 */
	type(type) {
		this.#type = type;
		return this;
	}

	/**
	 * Set header html content
	 *
	 * @param content{string}
	 * @param closeButton{boolean}
	 *
	 * @returns {Modals}
	 */
	header(content = '', closeButton = true){
		this.#headerContent = `<div class="levcharity_modal_header levcharity_popup_modal_header">
			${content}
			${closeButton ? `<span class="levcharity_modal_close_button levcharity_popup_modal_close_button levcharity_modal_close_button_xmark levcharity_popup_modal_close_button_xmar">
				<svg viewBox="0 0 24 24">
					<path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
				</svg>
			</span>` : ''}
		</div>`

		this.#headerHasCloseButton = closeButton;

		return this;
	}

	/**
	 * Set body html content
	 *
	 * @param content{string}
	 *
	 * @returns {Modals}
	 */
	body(content = ''){
		this.#bodyContent = `<div class="levcharity_modal_body levcharity_popup_modal_body">
			${content}
		</div>`

		return this;
	}

	/**
	 * Set footer html content
	 *
	 * @param content{string}
	 *
	 * @returns {Modals}
	 */
	footer(content = ''){
		this.#footerContent = `<div class="levcharity_modal_footer levcharity_popup_modal_footer">
			${content}
		</div>`

		return this;
	}

	show() {
		this.backdrop = document.createElement('div');
		const backdropClasses = this.#className.split(' ').filter(Boolean).map(_className => 'levcharity_modal_backdrop--' + _className);
		this.backdrop.classList.add('levcharity_modal_backdrop', 'opacity', ...backdropClasses);
		this.modal = document.createElement('div');
		this.modal.classList.add('levcharity_modal', 'levcharity_popup_modal', ...this.#className.split(' ').filter(Boolean))
		this.modal.innerHTML = this.#headerContent + this.#bodyContent + this.#footerContent;

		this.backdrop.append(this.modal);

		document.body.append(this.backdrop);

		this.#addEvents();
		this.animate(true);

		return this;
	}

	hide(context = '', event, target) {
		this.animate(false, () => {
			this.#onClose(context, event, target);
			this.modal.remove();
			this.backdrop.remove();
		});

		return this;
	}

	#addEvents() {
		DOM.addEvent('click', this.backdrop, (event, backdrop) => {
			if (this.#closeOnOutsideClick && event.target === this.backdrop) {
				event.stopPropagation();
				this.#handleClose('outside', event, backdrop);
			}
		});

		DOM.addEvent('click', '.levcharity_modal_close_button', (event, closeButton) => {
			if (this.#headerHasCloseButton) {
				this.#handleClose('header', event, closeButton);
			}
		}, this.modal);
	}

	#handleClose(context = '', event, target) {
		this.hide(context, event, target);
	}

	async animate(show, onComplete = () => {}) {
		const {gsap} = await import(/* webpackChunkName: "gsap" */ 'gsap')
		if (show) {
			let anim;
			this.backdrop?.classList.add('opacity');
			setTimeout(() => {
				this.backdrop?.classList.remove('opacity')
			})
			switch (this.#animation) {
				case 'ltr':
					anim = { x: '-10%', opacity: 0 };
					break;
				case 'rtl':
					anim = { x: '10%', opacity: 0 };
					break;
				case 'ttb':
					anim = { y: '-10%', opacity: 0 };
					break;
				case 'btt':
					anim = { y: '10%', opacity: 0 };
					break;
				case 'scale':
					anim = { scale: .85, opacity: 0 };
					break;
				default:
					anim = { opacity: 0 };
			}

			gsap.fromTo(this.modal, anim, {
				x: 0,
				y: 0,
				scale: 1,
				opacity: 1,
				duration: 0.3,
				onComplete,
			});
		} else {
			let anim;
			this.backdrop?.classList.add('opacity')
			switch (this.#animation) {
				case 'ltr':
					anim = { x: '-10%', opacity: 0 };
					break;
				case 'rtl':
					anim = { x: '10%', opacity: 0 };
					break;
				case 'ttb':
					anim = { y: '-10%', opacity: 0 };
					break;
				case 'btt':
					anim = { y: '10%', opacity: 0 };
					break;
				case 'scale':
					anim = { scale: .7, opacity: 0 };
					break;
				default:
					anim = { opacity: 0 };
			}

			gsap.to(this.modal, {
				...anim,
				duration: 0.3,
				onComplete,
			});
		}

		return this;
	}
}
